import {
  ImsPermissions,
  MarketingPermissions,
  OrderManagementPermissions,
  SettingsPermissions,
  SupportPermissions,
  type UserPermissions,
} from "@redotech/account-common/permissions";
import {
  ManagedUserRoles,
  type ManagedUserRolesType,
} from "@redotech/account-common/roles";

export const NotificationSource = {
  RETURNS: "returns",
  ORDER_TRACKING: "order-tracking",
  OUTBOUND_LABELS: "outbound-labels",
  INVENTORY: "inventory",
  SUPPORT: "support",
  SYSTEM: "system",
} as const;

export type NotificationSourceType =
  (typeof NotificationSource)[keyof typeof NotificationSource];

export const NotificationKind = {
  RETURN_CREATED: "return-created",
  RETURN_DELIVERED: "return-delivered",
  RETURN_REVIEW: "return-review",
  CLAIM_CREATED: "claim-created",
  CLAIM_DELIVERED: "claim-delivered",
  CLAIM_REVIEW: "claim-review",
  WARRANTY_CREATED: "warranty-created",
  WARRANTY_DELIVERED: "warranty-delivered",
  WARRANTY_REVIEW: "warranty-review",
  ORDER_TRACKING_STALLED: "order-tracking-stalled",
  ORDER_TRACKING_DELAYED: "order-tracking-delayed",
  ORDER_TRACKING_RETURN_TO_SENDER: "order-tracking-return-to-sender",
  ORDER_TRACKING_DELIVERY_ATTEMPT: "order-tracking-delivery-attempt",
  ORDER_TRACKING_DELIVERY_FAILED: "order-tracking-delivery-failed",
  ORDER_TRACKING_DELIVERY_CANCELLED: "order-tracking-delivery-cancelled",
  ORDER_TRACKING_DELIVERY_ERROR: "order-tracking-delivery-error",
  OUTBOUND_LABELS_FUNDS_LOW: "outbound-labels-funds-low",
  OUTBOUND_LABELS_CLAIM_PROCESSED: "outbound-labels-claim-processed",
  OUTBOUND_LABELS_SURCHARGE_DISPUTE_UPDATE:
    "outbound-labels-surcharge-dispute-update",
  INVENTORY_LOW_STOCK: "inventory-low-stock",
  SUPPORT: "support",
  TASK: "task",
  REPORT: "report",
  BILLING: "billing",
  ADMIN: "admin",
} as const;

export type NotificationKindType =
  (typeof NotificationKind)[keyof typeof NotificationKind];

export interface NotificationEligibility {
  readonly requiredPermissions?: readonly UserPermissions[];
  readonly managedRoles?: readonly ManagedUserRolesType[];
  readonly canUnsubscribe: boolean;
  readonly canEnableEmail: boolean;
  readonly canEnableSms: boolean;
}

export interface NotificationAudienceDefaults {
  readonly managedRoles?: readonly ManagedUserRolesType[];
  readonly includeActor?: boolean;
}

export interface NotificationDeliveryDefaults {
  readonly subscribed: boolean;
  readonly emailEnabled: boolean;
  readonly smsEnabled: boolean;
}

export interface NotificationDefinition {
  readonly source: NotificationSourceType;
  readonly kind: NotificationKindType;
  readonly eligibility: NotificationEligibility;
  readonly audience: NotificationAudienceDefaults;
  readonly defaults: NotificationDeliveryDefaults;
}

type NotificationDefinitionConfig = Omit<
  NotificationDefinition,
  "source" | "kind"
>;

type NotificationDefinitionsBySource = {
  readonly [source in NotificationSourceType]?: {
    readonly [kind in NotificationKindType]?: NotificationDefinitionConfig;
  };
};

const adminOnlyEligibility: NotificationEligibility = {
  managedRoles: [ManagedUserRoles.ADMIN],
  canUnsubscribe: true,
  canEnableEmail: true,
  canEnableSms: false,
};

const orderManagementEligibility: NotificationEligibility = {
  requiredPermissions: [OrderManagementPermissions.RECEIVE_ORDER_UPDATES],
  canUnsubscribe: true,
  canEnableEmail: true,
  canEnableSms: false,
};

const supportEligibility: NotificationEligibility = {
  requiredPermissions: [SupportPermissions.VIEW_SUPPORT],
  managedRoles: [
    ManagedUserRoles.ADMIN,
    ManagedUserRoles.USER,
    ManagedUserRoles.SUPPORT_REPLY_ONLY,
    ManagedUserRoles.SUPPORT_VIEW_ONLY,
  ],
  canUnsubscribe: true,
  canEnableEmail: true,
  canEnableSms: false,
};

const warehouseRoles = [
  ManagedUserRoles.ADMIN,
  ManagedUserRoles.WAREHOUSE_USER,
  ManagedUserRoles.WAREHOUSE_PICKER,
  ManagedUserRoles.WAREHOUSE_PACKER,
  ManagedUserRoles.WAREHOUSE_REPLENISHER,
] as const;

const inventoryRoles = [
  ManagedUserRoles.ADMIN,
  ManagedUserRoles.IMS_OPERATIONS,
  ManagedUserRoles.WAREHOUSE_USER,
  ManagedUserRoles.WAREHOUSE_REPLENISHER,
] as const;

const inAppOnlyDefaults: NotificationDeliveryDefaults = {
  subscribed: true,
  emailEnabled: false,
  smsEnabled: false,
};

const emailDefaultOn: NotificationDeliveryDefaults = {
  subscribed: true,
  emailEnabled: true,
  smsEnabled: false,
};

function definition(
  input: NotificationDefinitionConfig,
): NotificationDefinitionConfig {
  return input;
}

export const notificationDefinitions = {
  [NotificationSource.RETURNS]: {
    [NotificationKind.RETURN_CREATED]: definition({
      eligibility: orderManagementEligibility,
      audience: {
        managedRoles: [ManagedUserRoles.ADMIN, ManagedUserRoles.USER],
      },
      defaults: inAppOnlyDefaults,
    }),
    [NotificationKind.RETURN_DELIVERED]: definition({
      eligibility: orderManagementEligibility,
      audience: {
        managedRoles: [ManagedUserRoles.ADMIN, ManagedUserRoles.USER],
      },
      defaults: inAppOnlyDefaults,
    }),
    [NotificationKind.RETURN_REVIEW]: definition({
      eligibility: orderManagementEligibility,
      audience: {
        managedRoles: [ManagedUserRoles.ADMIN, ManagedUserRoles.USER],
      },
      defaults: inAppOnlyDefaults,
    }),
    [NotificationKind.CLAIM_CREATED]: definition({
      eligibility: orderManagementEligibility,
      audience: {
        managedRoles: [ManagedUserRoles.ADMIN, ManagedUserRoles.USER],
      },
      defaults: inAppOnlyDefaults,
    }),
    [NotificationKind.CLAIM_DELIVERED]: definition({
      eligibility: orderManagementEligibility,
      audience: {
        managedRoles: [ManagedUserRoles.ADMIN, ManagedUserRoles.USER],
      },
      defaults: inAppOnlyDefaults,
    }),
    [NotificationKind.CLAIM_REVIEW]: definition({
      eligibility: orderManagementEligibility,
      audience: {
        managedRoles: [ManagedUserRoles.ADMIN, ManagedUserRoles.USER],
      },
      defaults: inAppOnlyDefaults,
    }),
    [NotificationKind.WARRANTY_CREATED]: definition({
      eligibility: orderManagementEligibility,
      audience: {
        managedRoles: [ManagedUserRoles.ADMIN, ManagedUserRoles.USER],
      },
      defaults: inAppOnlyDefaults,
    }),
    [NotificationKind.WARRANTY_DELIVERED]: definition({
      eligibility: orderManagementEligibility,
      audience: {
        managedRoles: [ManagedUserRoles.ADMIN, ManagedUserRoles.USER],
      },
      defaults: inAppOnlyDefaults,
    }),
    [NotificationKind.WARRANTY_REVIEW]: definition({
      eligibility: orderManagementEligibility,
      audience: {
        managedRoles: [ManagedUserRoles.ADMIN, ManagedUserRoles.USER],
      },
      defaults: inAppOnlyDefaults,
    }),
  },
  [NotificationSource.ORDER_TRACKING]: {
    [NotificationKind.ORDER_TRACKING_STALLED]: definition({
      eligibility: {
        requiredPermissions: [
          MarketingPermissions.VIEW_ORDER_TRACKING_ANALYTICS,
        ],
        canUnsubscribe: true,
        canEnableEmail: true,
        canEnableSms: false,
      },
      audience: {
        managedRoles: [ManagedUserRoles.ADMIN, ManagedUserRoles.USER],
      },
      defaults: inAppOnlyDefaults,
    }),
    [NotificationKind.ORDER_TRACKING_DELAYED]: definition({
      eligibility: {
        requiredPermissions: [
          MarketingPermissions.VIEW_ORDER_TRACKING_ANALYTICS,
        ],
        canUnsubscribe: true,
        canEnableEmail: true,
        canEnableSms: false,
      },
      audience: {
        managedRoles: [ManagedUserRoles.ADMIN, ManagedUserRoles.USER],
      },
      defaults: inAppOnlyDefaults,
    }),
    [NotificationKind.ORDER_TRACKING_RETURN_TO_SENDER]: definition({
      eligibility: orderManagementEligibility,
      audience: {
        managedRoles: [ManagedUserRoles.ADMIN, ManagedUserRoles.USER],
      },
      defaults: inAppOnlyDefaults,
    }),
    [NotificationKind.ORDER_TRACKING_DELIVERY_ATTEMPT]: definition({
      eligibility: orderManagementEligibility,
      audience: {
        managedRoles: [ManagedUserRoles.ADMIN, ManagedUserRoles.USER],
      },
      defaults: inAppOnlyDefaults,
    }),
    [NotificationKind.ORDER_TRACKING_DELIVERY_FAILED]: definition({
      eligibility: orderManagementEligibility,
      audience: {
        managedRoles: [ManagedUserRoles.ADMIN, ManagedUserRoles.USER],
      },
      defaults: inAppOnlyDefaults,
    }),
    [NotificationKind.ORDER_TRACKING_DELIVERY_CANCELLED]: definition({
      eligibility: orderManagementEligibility,
      audience: {
        managedRoles: [ManagedUserRoles.ADMIN, ManagedUserRoles.USER],
      },
      defaults: inAppOnlyDefaults,
    }),
    [NotificationKind.ORDER_TRACKING_DELIVERY_ERROR]: definition({
      eligibility: orderManagementEligibility,
      audience: {
        managedRoles: [ManagedUserRoles.ADMIN, ManagedUserRoles.USER],
      },
      defaults: inAppOnlyDefaults,
    }),
  },
  [NotificationSource.OUTBOUND_LABELS]: {
    [NotificationKind.OUTBOUND_LABELS_FUNDS_LOW]: definition({
      eligibility: {
        requiredPermissions: [OrderManagementPermissions.MANAGE_SHIPMENTS],
        managedRoles: [
          ManagedUserRoles.ADMIN,
          ManagedUserRoles.WAREHOUSE_USER,
          ManagedUserRoles.IMS_FINANCE,
        ],
        canUnsubscribe: true,
        canEnableEmail: true,
        canEnableSms: false,
      },
      audience: {
        managedRoles: [
          ManagedUserRoles.ADMIN,
          ManagedUserRoles.WAREHOUSE_USER,
          ManagedUserRoles.IMS_FINANCE,
        ],
      },
      defaults: emailDefaultOn,
    }),
    [NotificationKind.OUTBOUND_LABELS_CLAIM_PROCESSED]: definition({
      eligibility: orderManagementEligibility,
      audience: {
        managedRoles: [ManagedUserRoles.ADMIN, ManagedUserRoles.USER],
      },
      defaults: inAppOnlyDefaults,
    }),
    [NotificationKind.OUTBOUND_LABELS_SURCHARGE_DISPUTE_UPDATE]: definition({
      eligibility: orderManagementEligibility,
      audience: {
        managedRoles: [ManagedUserRoles.ADMIN, ManagedUserRoles.USER],
      },
      defaults: inAppOnlyDefaults,
    }),
  },
  [NotificationSource.INVENTORY]: {
    [NotificationKind.INVENTORY_LOW_STOCK]: definition({
      eligibility: {
        requiredPermissions: [ImsPermissions.ACCESS_INVENTORY],
        managedRoles: inventoryRoles,
        canUnsubscribe: true,
        canEnableEmail: true,
        canEnableSms: false,
      },
      audience: { managedRoles: inventoryRoles },
      defaults: inAppOnlyDefaults,
    }),
  },
  [NotificationSource.SUPPORT]: {
    [NotificationKind.SUPPORT]: definition({
      eligibility: supportEligibility,
      audience: {
        managedRoles: [
          ManagedUserRoles.ADMIN,
          ManagedUserRoles.USER,
          ManagedUserRoles.SUPPORT_REPLY_ONLY,
        ],
      },
      defaults: inAppOnlyDefaults,
    }),
  },
  [NotificationSource.SYSTEM]: {
    [NotificationKind.TASK]: definition({
      eligibility: orderManagementEligibility,
      audience: { managedRoles: warehouseRoles },
      defaults: inAppOnlyDefaults,
    }),
    [NotificationKind.REPORT]: definition({
      eligibility: {
        canUnsubscribe: true,
        canEnableEmail: true,
        canEnableSms: false,
      },
      audience: { includeActor: true },
      defaults: emailDefaultOn,
    }),
    [NotificationKind.BILLING]: definition({
      eligibility: {
        requiredPermissions: [SettingsPermissions.VIEW_BILLING],
        managedRoles: [ManagedUserRoles.ADMIN],
        canUnsubscribe: true,
        canEnableEmail: true,
        canEnableSms: false,
      },
      audience: { managedRoles: [ManagedUserRoles.ADMIN] },
      defaults: emailDefaultOn,
    }),
    [NotificationKind.ADMIN]: definition({
      eligibility: adminOnlyEligibility,
      audience: { managedRoles: [ManagedUserRoles.ADMIN] },
      defaults: inAppOnlyDefaults,
    }),
  },
} as const satisfies NotificationDefinitionsBySource;

export function getNotificationDefinition(
  source: NotificationSourceType,
  kind: NotificationKindType,
): NotificationDefinition | undefined {
  const definitionsByKind = notificationDefinitions[source] as
    | Partial<Record<NotificationKindType, NotificationDefinitionConfig>>
    | undefined;
  const config = definitionsByKind?.[kind];
  if (!config) {
    return undefined;
  }
  return { source, kind, ...config };
}
